import React from 'react';
import { colors } from '@config/colors';

/*
 * The three ways a wish makes the assistant better — the engines How we
 * build attaches one at a time, and the rows of every demo's table — so
 * the two sections name, colour and draw them alike. In June's order
 * (2026-09-22): the data first, then the expertise, then what the
 * assistant can do.
 *
 * Their colours come from one family, zen (June, 2026-09-23, after the
 * warm families and then pink, purple and magenta): the data centred on
 * its light, the expertise on its middle, the capability on its dark.
 * - `light`: the anchor — lines, arcs, rings, rules.
 * - `fill`: a gradient for a shape an icon sits on, and `iconInk` the
 *   icon's colour on it (4.3:1 or better on every stop).
 * - `pill` / `pillInk`: a label's background and its text, 4.7:1 or
 *   better. Zen's middle (#1F9AA7) carries small text in neither ink nor
 *   white, so the middle pill runs a step darker, in white.
 */
export const ENGINE_KINDS = {
  data: {
    label: 'Pre-fetched data',
    light: colors.accent.zen.light,
    fill: `linear-gradient(135deg, ${colors.accent.zen.extraLight}, ${colors.accent.zen.light})`,
    iconInk: colors.neutral.extraDark,
    pill: `linear-gradient(135deg, ${colors.accent.zen.extraLight}, ${colors.accent.zen.light})`,
    pillInk: colors.neutral.extraDark,
  },
  expert: {
    label: 'Domain expert models',
    light: colors.accent.zen.base,
    fill: `linear-gradient(135deg, ${colors.accent.zen.light}, ${colors.accent.zen.base})`,
    iconInk: colors.neutral.extraDark,
    pill: `linear-gradient(135deg, color-mix(in srgb, ${colors.accent.zen.dark} 70%, ${colors.accent.zen.base}), ${colors.accent.zen.dark})`,
    pillInk: colors.white,
  },
  skills: {
    label: 'Assistant capabilities',
    light: colors.accent.zen.extraDark,
    fill: `linear-gradient(135deg, ${colors.accent.zen.dark}, ${colors.accent.zen.extraDark})`,
    iconInk: colors.white,
    pill: `linear-gradient(135deg, ${colors.accent.zen.extraDark}, ${colors.accent.zen.exxtraDark})`,
    pillInk: colors.white,
  },
} as const;

export type EngineKey = keyof typeof ENGINE_KINDS;

export const ENGINE_ORDER: readonly EngineKey[] = ['data', 'expert', 'skills'];

const GRID = [6, 12, 18].flatMap((x) => [6, 12, 18].map((y) => ({ x, y, key: `${x}-${y}` })));

/** The data a grid of points, the expertise a chart, the capability a checked-off calendar. */
export const EngineIcon: React.FC<{ engine: EngineKey; size?: number; color?: string }> = ({
  engine,
  size = 30,
  color = colors.neutral.extraDark,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {engine === 'data' && (
      <g>
        {GRID.map((dot) => (
          <circle key={dot.key} cx={dot.x} cy={dot.y} r="1.6" fill={color} stroke="none" />
        ))}
      </g>
    )}
    {engine === 'expert' && (
      <g>
        <path d="M5 19v-6M12 19V5M19 19v-9" />
        <path d="M3 19h18" />
      </g>
    )}
    {engine === 'skills' && (
      <g>
        <rect x="3" y="5" width="18" height="16" rx="3" />
        <path d="M3 10h18M8 3v4M16 3v4" />
        <path d="m9 15 2 2 4-4" />
      </g>
    )}
  </svg>
);
